import 'react-dates/initialize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import DatePickerPopup from './_partials/DatePickerPopup';
import TimeSelect from './_partials/TimeSelect';
import Flex from '@wix/da-ds/pkg/Flex';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';

import s from './ScheduleTimeForm.scss';

interface Props {
  initialTime?: number;
  fieldGap?: number;
  fieldClassName?: string;
  dateInputLabel?: string;
  timeInputLabel?: string;
  noBottomSpacing?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (ts?: number) => void;
  dropDirection?: 'up' | 'down';
}

const ScheduleTimeForm: React.FC<Props> = ({
  initialTime,
  fieldGap = 16,
  fieldClassName,
  dateInputLabel,
  timeInputLabel,
  noBottomSpacing = true,
  isRequired,
  disabled,
  errorMessage,
  onChange,
  dropDirection,
}) => {
  const { t } = useTranslation();
  const [isPickerVisible, setPickerVisible] = useState(false);

  const today = useMemo(() => moment().startOf('day'), []);
  const threeMonthsOut = useMemo(() => moment().add(89, 'days'), []);
  const initialDate = useMemo(
    () => (initialTime ? moment.unix(initialTime) : undefined),
    [initialTime]
  );
  const [selectedDate, setSelectedDate] = useState<moment.Moment | undefined>(
    initialDate
  );
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    initialDate && `${initialDate.hour()}`
  );

  const toTs = (date, hour) =>
    date && hour ? moment(date).set('hour', Number(hour)).unix() : undefined;

  const handleDateChange = useCallback(
    (date: moment.Moment) => {
      setSelectedDate(date);
      setSelectedTime('');
      onChange(undefined); // changing date resets time, thus we don't have a complete value
    },
    [onChange]
  );

  const isOutsideRange = useCallback(
    (date: moment.Moment) => !date.isBetween(today, threeMonthsOut),
    [today, threeMonthsOut]
  );

  const handleTimeChange = useCallback(
    (hour: string) => {
      setSelectedTime(hour);
      onChange(toTs(selectedDate, hour));
    },
    [selectedDate, onChange]
  );

  useEffect(() => {
    if (initialTime) {
      const newMoment = moment.unix(initialTime);
      setSelectedDate(newMoment);
      setSelectedTime(`${newMoment.hour()}`);
    }
  }, [initialTime]);

  return (
    <div className={s['root']}>
      <Flex direction="row" gap={fieldGap}>
        <FormElement
          inputId="date-picker"
          label={
            <InputLabelText isRequired={isRequired}>
              {dateInputLabel}
            </InputLabelText>
          }
          isLabelHidden={!dateInputLabel}
          noBottomSpacing
          className={c(s['form-field'], fieldClassName)}
        >
          <DatePickerPopup
            id="schedule-draft-date-picker"
            placeholder={t('studio.modals.schedule.date.placeholder')}
            className={s['input']}
            date={selectedDate}
            isFocused={isPickerVisible}
            isOutsideRange={isOutsideRange}
            onFocusChange={({ focused }) => setPickerVisible(focused)}
            onDateChange={handleDateChange}
            disabled={disabled}
            dropDirection={dropDirection}
          />
        </FormElement>
        <FormElement
          inputId="date-picker"
          label={
            <InputLabelText isRequired={isRequired}>
              {timeInputLabel}
            </InputLabelText>
          }
          isLabelHidden={!timeInputLabel}
          noBottomSpacing
          className={c(s['form-field'], fieldClassName)}
        >
          <TimeSelect
            className={s['input']}
            selectedDate={selectedDate}
            value={selectedTime}
            onSelect={handleTimeChange}
            disabled={disabled}
          />
        </FormElement>
      </Flex>
      {!noBottomSpacing && (
        <div className={s['error-placeholder']}>
          {errorMessage && (
            <InputErrorMessage>{errorMessage}</InputErrorMessage>
          )}
        </div>
      )}
    </div>
  );
};
ScheduleTimeForm.displayName = 'ScheduleTimeForm';

export default ScheduleTimeForm;
